import { TypedDocumentNode, gql } from '@apollo/client';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import placeholder from '@sorare/core/src/assets/club/shield_none.png';
import { Input } from '@sorare/core/src/atoms/inputs/Input';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { ClubSelectionDialog } from './ClubSelectionDialog';
import {
  OnboardingClubSelectionQuery,
  OnboardingClubSelectionQueryVariables,
} from './__generated__/index.graphql';

const inputPlaceholder = defineMessage({
  id: 'Onboarding.FavoriteClubSelection.inputPlaceholder',
  defaultMessage: 'Search club',
});

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  width: 100%;
  margin: auto;

  @media ${tabletAndAbove} {
    width: 80%;
  }
  @media ${desktopAndAbove} {
    width: 60%;
  }
`;
const ClubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: var(--double-unit);
`;
const Club = styled(Vertical).attrs({ center: true, gap: 2 })`
  width: 100%;
  height: 100%;
  background: var(--c-black);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-100);
  padding: var(--triple-unit);
`;

const ONBOARDING_CLUB_SELECTION_QUERY = gql`
  query OnboardingClubSelectionQuery($query: String, $after: String) {
    football {
      rivals {
        id
        onboardingAvailableFavoriteTeams(
          query: $query
          after: $after
          first: 100
        ) {
          nodes {
            slug
            id
            name
            pictureUrl
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  OnboardingClubSelectionQuery,
  OnboardingClubSelectionQueryVariables
>;

export const FavoriteClubSelection = () => {
  const [open, setOpen] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const { formatMessage } = useIntl();

  const { data, loading, loadMore } = usePaginatedQuery(
    ONBOARDING_CLUB_SELECTION_QUERY,
    {
      connection: 'TeamInterfaceConnection',
      variables: { query },
    }
  );
  const { nodes: clubs = [], pageInfo } =
    data?.football.rivals.onboardingAvailableFavoriteTeams || {};
  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: pageInfo?.endCursor,
      });
    },
    !!pageInfo?.hasNextPage,
    loading
  );

  useDebounce(
    () => {
      setQuery(inputValue);
    },
    500,
    [inputValue]
  );

  const onClick = (slug: string) => {
    setSelectedSlug(slug);
    setOpen(true);
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  return (
    <>
      <Root>
        <HeadlineL>
          <FormattedMessage
            id="Onboarding.FavoriteClubSelection.title"
            defaultMessage="Select your favorite club"
          />
        </HeadlineL>
        <Input
          placeholder={formatMessage(inputPlaceholder)}
          value={inputValue}
          onChange={onSearchChange}
        />
        <ClubGrid>
          {clubs.map(({ slug, name, pictureUrl }) => (
            <button key={slug} type="button" onClick={() => onClick(slug)}>
              <Club>
                <img src={pictureUrl || placeholder} alt="" width={32} />
                <LabelS bold>{name}</LabelS>
              </Club>
            </button>
          ))}
        </ClubGrid>
        <InfiniteScrollLoader />
      </Root>
      <ClubSelectionDialog
        clubSlug={selectedSlug}
        open={!!open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
